import React, { useEffect } from 'react';
import { XMarkIcon, CommandLineIcon } from '@heroicons/react/24/outline';

const KeyboardShortcutsHelp = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      const handleEscape = (e) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const shortcuts = [
    {
      category: 'Navigation',
      items: [
        { keys: ['Ctrl', 'K'], description: 'Open global search' },
        { keys: ['?'], description: 'Show keyboard shortcuts' },
        { keys: ['Esc'], description: 'Close modal/dialog' },
      ]
    },
    {
      category: 'Sessions',
      items: [
        { keys: ['/', 'F'], description: 'Focus search box' },
        { keys: ['Ctrl', 'Enter'], description: 'Submit form' },
      ]
    },
    {
      category: 'Transcript Editor',
      items: [
        { keys: ['Ctrl', 'S'], description: 'Save transcript' },
        { keys: ['Ctrl', 'Z'], description: 'Undo' },
        { keys: ['Ctrl', 'Y'], description: 'Redo' },
        { keys: ['Ctrl', 'F'], description: 'Find in transcript' },
        { keys: ['Ctrl', 'Enter'], description: 'Add new segment' },
      ]
    },
    {
      category: 'General',
      items: [
        { keys: ['Tab'], description: 'Navigate between fields' },
        { keys: ['Enter'], description: 'Activate button/link' },
      ]
    }
  ];

  const renderKeys = (keys) => {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    return (
      <div className="flex items-center gap-1">
        {keys.map((key, index) => (
          <React.Fragment key={index}>
            {index > 0 && <span className="text-gray-400">+</span>}
            <kbd className="px-2 py-1 text-xs font-semibold text-gray-800">
              {key === 'Ctrl' && isMac ? '⌘' : key === 'Ctrl' ? 'Ctrl' : key === 'Enter' ? 'Enter' : key}
            </kbd>
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={onClose}>
      <div
        className="bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <CommandLineIcon className="h-6 w-6 text-primary-600" />
            <h2 className="text-xl font-bold text-gray-900">
              Keyboard Shortcuts
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="space-y-6">
            {shortcuts.map((category) => (
              <div key={category.category}>
                <h3 className="text-sm font-semibold text-gray-700">
                  {category.category}
                </h3>
                <div className="space-y-2">
                  {category.items.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between py-2 px-3 bg-gray-50"
                    >
                      <span className="text-sm text-gray-600">
                        {item.description}
                      </span>
                      {renderKeys(item.keys)}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              Press <kbd className="px-1.5 py-0.5 text-xs font-semibold text-gray-800">Esc</kbd> to close
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeyboardShortcutsHelp;
